import React, { useState, useRef, useEffect } from 'react';
import {
  Send,
  Bot,
  User,
  Search,
  MessageSquare,
  Paperclip,
  Mic,
  Camera,
  Stethoscope,
  FileText,
  Clock,
  Settings,
  UserCircle,
  ChevronLeft,
  ChevronRight,
  Pill,
  TestTube,
  Calendar,
  Phone,
  Mail,
  Home,
  Menu
} from 'lucide-react';
import { aiService, ChatMessage } from '../../services';
import { TypingMessage } from '../../components/molecules/TypingMessage';

interface Patient {
  id: string;
  name: string;
  species: 'dog' | 'cat' | 'bird' | 'rabbit' | 'other';
  breed?: string;
  age?: string;
  owner?: string;
  lastVisit?: string;
}

const AIChatFullPage: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState<'rag' | 'non-rag'>('rag');
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Mock data untuk demo
  const recentConversations = [
    { id: '1', title: 'Kucing saya diare', timestamp: '2 jam lalu', patient: 'Luna' },
    { id: '2', title: 'Vaksinasi anjing', timestamp: '1 hari lalu', patient: 'Max' },
    { id: '3', title: 'Checkup rutin kelinci', timestamp: '3 hari lalu', patient: 'Snowball' },
  ];

  const quickActions = [
    { icon: Pill, label: 'Drug Information', color: 'bg-blue-500' },
    { icon: TestTube, label: 'Lab Results', color: 'bg-green-500' },
    { icon: Stethoscope, label: 'Symptoms Checker', color: 'bg-purple-500' },
    { icon: Calendar, label: 'Appointment', color: 'bg-orange-500' },
    { icon: FileText, label: 'Medical Records', color: 'bg-pink-500' },
    { icon: Phone, label: 'Emergency', color: 'bg-red-500' },
  ];

  const mockPatients: Patient[] = [
    { id: '1', name: 'Luna', species: 'cat', breed: 'Persian', age: '3 tahun', owner: 'John Doe', lastVisit: '2 hari lalu' },
    { id: '2', name: 'Max', species: 'dog', breed: 'Golden Retriever', age: '5 tahun', owner: 'Jane Smith', lastVisit: '1 minggu lalu' },
    { id: '3', name: 'Snowball', species: 'rabbit', age: '2 tahun', owner: 'Mike Johnson', lastVisit: '3 hari lalu' },
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const generateId = () => {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: generateId(),
      content: inputMessage.trim(),
      role: 'user',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await aiService.sendMessage(
        userMessage.content,
        mode,
        conversationId
      );

      if (response.conversation_id && !conversationId) {
        setConversationId(response.conversation_id);
      }

      const assistantMessage: ChatMessage = {
        id: response.message_id || generateId(),
        content: response.response,
        role: 'assistant',
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: ChatMessage = {
        id: generateId(),
        content: 'Maaf, terjadi kesalahan saat mengirim pesan. Silakan coba lagi.',
        role: 'assistant',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      // Handle file upload logic
      console.log('Files uploaded:', files);
    }
  };

  const clearConversation = () => {
    setMessages([]);
    setConversationId(undefined);
  };

  const navigateBackToApp = () => {
    window.location.href = '/dashboard';
  };

  // Add keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl/Cmd + B to toggle sidebar
      if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
        e.preventDefault();
        setSidebarCollapsed(prev => !prev);
      }
      // Escape to go back to main app
      if (e.key === 'Escape') {
        navigateBackToApp();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <div className={`${sidebarCollapsed ? 'w-16' : 'w-80'} bg-white border-r border-gray-200 flex flex-col transition-all duration-300`}>
        {/* Navigation Header */}
        <div className={`p-4 border-b border-gray-200 ${sidebarCollapsed ? 'flex justify-center' : ''}`}>
          <div className={`flex items-center ${sidebarCollapsed ? 'flex-col space-y-2' : 'justify-between'}`}>
            <button
              onClick={navigateBackToApp}
              className={`flex items-center space-x-2 p-2 hover:bg-gray-100 rounded-lg transition-colors ${
                sidebarCollapsed ? 'justify-center' : ''
              }`}
              title="Back to VetPintar (ESC)"
            >
              <Home className="w-4 h-4 text-gray-600" />
              {!sidebarCollapsed && <span className="text-sm text-gray-700">Back to App</span>}
            </button>

            {!sidebarCollapsed && (
              <h1 className="text-lg font-semibold text-gray-900">AI Assistant</h1>
            )}
          </div>
        </div>
        {/* VetPintar Logo Section */}
        <div className={`p-4 ${sidebarCollapsed ? 'flex justify-center' : ''}`}>
          <div className={`flex items-center ${sidebarCollapsed ? '' : 'space-x-3'}`}>
            <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
              <Stethoscope className="w-6 h-6 text-white" />
            </div>
            {!sidebarCollapsed && (
              <div>
                <h2 className="font-semibold text-gray-900">VetPintar AI</h2>
                <p className="text-xs text-gray-500">Veterinary Assistant</p>
              </div>
            )}
            {!sidebarCollapsed && (
              <button
                onClick={() => setSidebarCollapsed(true)}
                className="ml-auto p-1 hover:bg-gray-100 rounded-md transition-colors"
                title="Collapse sidebar (Ctrl+B)"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Expand button - always visible when collapsed */}
        {sidebarCollapsed && (
          <div className="p-2 border-b border-gray-200">
            <button
              onClick={() => setSidebarCollapsed(false)}
              className="w-full p-2 hover:bg-gray-100 rounded-md transition-colors flex justify-center"
              title="Expand sidebar (Ctrl+B)"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {!sidebarCollapsed && (
          <>
            {/* Quick Actions */}
            <div className="p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Quick Actions</h3>
              <div className="grid grid-cols-2 gap-2">
                {quickActions.map((action, index) => {
                  const Icon = action.icon;
                  return (
                    <button
                      key={index}
                      className={`${action.color} text-white p-3 rounded-lg hover:opacity-90 transition-opacity flex flex-col items-center space-y-1`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-xs text-center">{action.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Patient Context */}
            <div className="p-4 border-t border-gray-200">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Patient Context</h3>
              <select
                className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                onChange={(e) => setSelectedPatient(mockPatients.find(p => p.id === e.target.value) || null)}
              >
                <option value="">Select Patient</option>
                {mockPatients.map(patient => (
                  <option key={patient.id} value={patient.id}>
                    {patient.name} - {patient.species} ({patient.owner})
                  </option>
                ))}
              </select>

              {selectedPatient && (
                <div className="mt-3 p-3 bg-blue-50 rounded-md">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-8 h-8 bg-blue-200 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium">
                        {selectedPatient.species === 'dog' ? '🐕' :
                         selectedPatient.species === 'cat' ? '🐈' :
                         selectedPatient.species === 'rabbit' ? '🐰' : '🦜'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-sm">{selectedPatient.name}</p>
                      <p className="text-xs text-gray-600">{selectedPatient.breed} • {selectedPatient.age}</p>
                    </div>
                  </div>
                  <div className="text-xs text-gray-600">
                    <p>Owner: {selectedPatient.owner}</p>
                    <p>Last visit: {selectedPatient.lastVisit}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Recent Conversations */}
            <div className="flex-1 p-4 border-t border-gray-200 overflow-y-auto">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Recent Conversations</h3>
              <div className="space-y-2">
                {recentConversations.map(conv => (
                  <button
                    key={conv.id}
                    className="w-full text-left p-3 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{conv.title}</p>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className="text-xs text-gray-500">{conv.patient}</span>
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-xs text-gray-400">{conv.timestamp}</span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className={`flex items-center space-x-2 ${sidebarCollapsed && 'justify-center'}`}>
              <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                <UserCircle className="w-5 h-5 text-gray-600" />
              </div>
              {!sidebarCollapsed && (
                <div>
                  <p className="text-sm font-medium">Dr. Sarah</p>
                  <p className="text-xs text-gray-500">VetPintar Clinic</p>
                </div>
              )}
            </div>
            {!sidebarCollapsed && (
              <button className="p-1 hover:bg-gray-100 rounded-md transition-colors">
                <Settings className="w-4 h-4 text-gray-600" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Floating Expand Button (when sidebar is collapsed) */}
      {sidebarCollapsed && (
        <button
          onClick={() => setSidebarCollapsed(false)}
          className="fixed left-4 top-1/2 transform -translate-y-1/2 z-20 w-10 h-10 bg-white border border-gray-200 rounded-lg shadow-md hover:shadow-lg hover:border-blue-300 hover:bg-blue-50 transition-all duration-200 flex items-center justify-center group"
          title="Expand sidebar (Ctrl+B)"
        >
          <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-blue-600" />
        </button>
      )}

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Veterinary AI Assistant</h1>
                <p className="text-sm text-gray-600">
                  {mode === 'rag'
                    ? 'AI with knowledge base access'
                    : 'AI with general knowledge'
                  }
                </p>
              </div>

              {/* Mode Toggle */}
              <div className="bg-gray-100 rounded-lg p-1 flex">
                <button
                  onClick={() => setMode('rag')}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    mode === 'rag'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Search className="w-4 h-4" />
                  {!sidebarCollapsed && <span>RAG</span>}
                </button>
                <button
                  onClick={() => setMode('non-rag')}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    mode === 'non-rag'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <MessageSquare className="w-4 h-4" />
                  {!sidebarCollapsed && <span>Chat</span>}
                </button>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={clearConversation}
                className="text-sm text-gray-600 hover:text-gray-900 px-3 py-1 rounded-md hover:bg-gray-100"
              >
                Clear Chat
              </button>
            </div>
          </div>
        </div>

        {/* Messages Container */}
        <div className="flex-1 overflow-y-auto bg-white">
          <div className="max-w-3xl mx-auto px-4 py-8">
            {messages.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Bot className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">
                  Welcome to VetPintar AI Assistant
                </h2>
                <p className="text-gray-600 max-w-md mx-auto mb-8">
                  Get professional veterinary guidance and support. Ask about symptoms, treatments,
                  drug information, or general pet care advice.
                </p>

                {/* Suggested Questions */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl mx-auto">
                  {[
                    "What are the symptoms of parvovirus in dogs?",
                    "How often should cats be vaccinated?",
                    "What should I do if my pet has diarrhea?",
                    "Common allergies in dogs and treatment"
                  ].map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => setInputMessage(suggestion)}
                      className="text-left p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
                    >
                      <p className="text-sm text-gray-700">{suggestion}</p>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-8 py-6">
                {messages.map((message, index) => (
                  <div key={message.id}>
                    {message.role === 'user' ? (
                      // User message (no typing effect) - ChatGPT style
                      <div className="flex w-full">
                        <div className="flex-1 min-w-0 mr-4">
                          <div className="flex justify-end">
                            <div className="inline-block max-w-2xl">
                              <div className="text-base leading-relaxed text-gray-800 dark:text-gray-100 text-right">
                                <div className="whitespace-pre-wrap">
                                  {message.content}
                                </div>
                              </div>
                              {/* Timestamp */}
                              <div className="text-xs text-gray-400 mt-2 text-right">
                                {message.timestamp.toLocaleTimeString('id-ID', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                        {/* Avatar */}
                        <div className="flex-shrink-0 w-8 h-8">
                          <div className="w-8 h-8 rounded-full bg-gray-600 text-white flex items-center justify-center">
                            <User className="w-5 h-5" />
                          </div>
                        </div>
                      </div>
                    ) : (
                      // AI message with typing effect
                      <TypingMessage
                        message={message}
                        onComplete={() => {
                          // Scroll to bottom when typing is complete
                          setTimeout(scrollToBottom, 100);
                        }}
                        typingSpeed={30}
                      />
                    )}
                  </div>
                ))}

                {/* Loading Indicator */}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="flex items-start space-x-4 max-w-3xl">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white flex items-center justify-center">
                        <Bot className="w-6 h-6" />
                      </div>
                      <div className="bg-white border border-gray-200 shadow-sm px-6 py-4 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="flex space-x-1">
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                          </div>
                          <span className="text-sm text-gray-600">AI Assistant is thinking...</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Enhanced Input Area */}
        <div className="bg-white border-t border-gray-200 px-6 py-4">
          <div className="max-w-4xl mx-auto">
            {/* Medical Tools Bar */}
            <div className="flex items-center space-x-2 mb-3">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                title="Attach medical records"
              >
                <Paperclip className="w-5 h-5" />
              </button>
              <button
                className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                title="Take photo"
              >
                <Camera className="w-5 h-5" />
              </button>
              <button
                className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                title="Voice input"
              >
                <Mic className="w-5 h-5" />
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <span className="text-xs text-gray-500">
                {selectedPatient ? `📋 Context: ${selectedPatient.name}` : 'No patient selected'}
              </span>
            </div>

            <form onSubmit={handleSendMessage} className="flex items-end space-x-4">
              <div className="flex-1">
                <textarea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask about symptoms, treatments, drug information, or general veterinary advice..."
                  disabled={isLoading}
                  rows={1}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none disabled:bg-gray-50 disabled:text-gray-400"
                  style={{
                    minHeight: '48px',
                    maxHeight: '120px',
                    height: 'auto',
                  }}
                  onInput={(e) => {
                    const target = e.target as HTMLTextAreaElement;
                    target.style.height = 'auto';
                    target.style.height = Math.min(target.scrollHeight, 120) + 'px';
                  }}
                />
              </div>
              <button
                type="submit"
                disabled={!inputMessage.trim() || isLoading}
                className="flex-shrink-0 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center space-x-2"
              >
                <Send className="w-5 h-5" />
                <span>Send</span>
              </button>
            </form>

            {/* Smart Suggestions */}
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs text-gray-500">
                Press Enter to send, Shift+Enter for new line
              </p>
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-500">Powered by</span>
                <div className="flex items-center space-x-1">
                  <div className="w-4 h-4 bg-blue-600 rounded"></div>
                  <span className="text-xs font-medium text-gray-700">Claude AI</span>
                </div>
              </div>
            </div>
          </div>

          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx"
            onChange={handleFileUpload}
            className="hidden"
          />
        </div>
      </div>
    </div>
  );
};

export default AIChatFullPage;